import round from 'lodash/round';
import { ArrayFilterItem, SingleValueFilterItem, StyleState } from 'components';
import { ProfileResponse } from '../../../module/profile/model/ProfileResponse';
import { Authority } from '../../../module/authority/model/Authority';
import { useSecurity } from '../hooks/useSecurity';
import Filter from '../../../components/types/Filter';
import { Role, RoleRank } from '../../../module/role/model/Role';
import { SESSION, webStorage } from '@common';
import { Product } from '@module/product/model/Product';
import { CustomState } from '@module/country/model/CountryResponse';
import { WebSession } from '../../../module/web-session/model/WebSession';

export const evaluateObjectFromString = (text: string, obj: any) => {
  return text
    .replace(/\[([^\]]+)]/g, '.$1')
    .split('.')
    .reduce((o, p) => o[p], obj);
};

export const isJson = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const NVL = (
  val: string | number | undefined,
  defaultValue: string = '-'
): string => {
  return !!val ? '' + val : defaultValue;
};

export const NVL_OBJ = (
  obj: any,
  property: string = 'label',
  defaultValue: string = '-'
) => {
  return obj && obj[property] ? obj[property] : defaultValue;
};

export const ROUND = (num: any) => {
  return round(num, 2);
};

export const formatDouble = (
  num: number | undefined,
  defaultValue: string = '0.00'
): string => {
  return num
    ? (Math.round(num * 100) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2
      })
    : defaultValue;
};

export const isInArray = (value: string, ...array: string[]): boolean => {
  return !!array.find((a) => a === value);
};

const customTypesToStyleMap: Map<CustomState, StyleState> = new Map<
  CustomState,
  StyleState
>([
  ['PENDING', 'warning'],
  ['RESOLVED', 'success'],
  ['VERIFIED', 'success'],
  ['DISCARDED', 'danger'],
  ['ACTIVE', 'success'],
  ['INACTIVE', 'danger'],
  ['NEW', 'primary'],
  ['ON', 'success'],
  ['SUCCESS', 'success'],
  ['FAILED', 'danger'],
  ['IGNORE', 'warning'],
  ['DRAFT', 'warning'],
  ['PUBLISHED', 'success'],
  ['ON_HOLD', 'primary'],
  ['IN_PROCESS', 'warning'],
  ['ERROR', 'danger']
]);

export const resolveState = (state: CustomState) => {
  return customTypesToStyleMap.get(state);
};

export const hasAuthority = (
  profile: ProfileResponse,
  authority: Authority
) => {
  return !!profile.authorities?.find(
    (auth) => auth.isEnabled && auth.authority === authority
  );
};

export const hasProductAndAuthority = (
  profile: ProfileResponse,
  product: Product,
  authority: Authority
) => {
  return hasProduct(profile, product) && hasAuthority(profile, authority);
};

export const hasProduct = (profile: ProfileResponse, product: Product) => {
  if (!!profile && !!profile.products && !!product) {
    for (const item of profile.products) if (item == product) return true;
  }
  return false;
};

export const checkAuthorities = (
  profile: ProfileResponse | null | undefined,
  includedAuthorities: Authority[],
  excludedAuthorities: Authority[]
) => {
  return (
    !!profile?.authorities &&
    includedAuthorities.every(
      (authority) =>
        profile.authorities.findIndex(
          (p) => p.authority === authority && p.isEnabled
        ) >= 0
    ) &&
    excludedAuthorities.every(
      (authority) =>
        profile.authorities.findIndex(
          (p) => p.authority === authority && p.isEnabled
        ) < 0
    )
  );
};

export const getCachedFilterName = (
  filterName: string,
  profileId?: string
): string => {
  return (
    filterName + '-' + (!!profileId ? profileId : useSecurity().getProfileId())
  );
};

export const retrieveCachedFilter = (
  cachedFilterName: string
): Filter | null => {
  const cachedFilter = webStorage.getItem<Filter>(cachedFilterName);
  return cachedFilter;
};

export const removeCachedFilter = (cachedFilterName: string) => {
  webStorage.removeItem(cachedFilterName);
};

export const updateCachedFilterArrayFilterItem = (
  filterName: string,
  arrayFilterItemToUpdate: ArrayFilterItem
) => {
  const cachedFilterName = filterName + '-filter';
  const cachedFilter = webStorage.getItem<Filter>(cachedFilterName);

  if (cachedFilter && cachedFilter.arrayFilterItems) {
    const idx = cachedFilter.arrayFilterItems.findIndex(
      (aft: ArrayFilterItem) => aft.name === arrayFilterItemToUpdate.name
    );
    cachedFilter.arrayFilterItems.splice(idx, idx >= 0 ? 1 : 0);
    cachedFilter.arrayFilterItems.push(arrayFilterItemToUpdate);
    webStorage.setItem(cachedFilterName, cachedFilter);
  } else {
    webStorage.setItem(cachedFilterName, {
      arrayFilterItems: [arrayFilterItemToUpdate],
      singleValueFilterItems: []
    });
  }
};

export const updateCachedFilterSingleFilterItem = (
  filterName: string,
  singleFilterItemToUpdate: SingleValueFilterItem
) => {
  const cachedFilterName = filterName + '-filter';
  const cachedFilter = webStorage.getItem<Filter>(cachedFilterName);

  if (cachedFilter) {
    if (!cachedFilter.singleValueFilterItems) {
      cachedFilter.singleValueFilterItems = [];
    }
    const idx = cachedFilter.singleValueFilterItems.findIndex(
      (aft: ArrayFilterItem) => aft.name === singleFilterItemToUpdate.name
    );
    cachedFilter.singleValueFilterItems.splice(idx, idx >= 0 ? 1 : 0);
    cachedFilter.singleValueFilterItems.push(singleFilterItemToUpdate);
    webStorage.setItem(cachedFilterName, cachedFilter);
  } else {
    webStorage.setItem(cachedFilterName, {
      arrayFilterItems: [],
      singleValueFilterItems: [singleFilterItemToUpdate]
    });
  }
};

export const clearCachedFiltersForProfile = () => {
  // Get profileId directly from storage instead of from useSecurity
  // so this can be called from different contexts
  const profileId = webStorage.getItem<WebSession>(SESSION)?.profile?.id;
  if (!profileId) return;

  // Get all -filter and -sorting cache entries of user and remove them from cache
  const keys: string[] = Object.keys(localStorage).filter(
    (key) =>
      key.includes(`${profileId}-filter`) ||
      key.includes(`${profileId}-sorting`)
  );
  for (const key of keys) {
    localStorage.removeItem(key);
  }
};

export const todayOrFutureDate = (date: Date): boolean => {
  return !!date && date.setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
};

export const isHigherOrEqualRole = (
  role: Role,
  roleToCompareWith: Role
): boolean => {
  return RoleRank[role] >= RoleRank[roleToCompareWith];
};

export const isBlank = (value: string): boolean => {
  return !value || /^\s*$/.test(value);
};
